import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput, {
  parsePhoneNumber,
  getCountryCallingCode,
  isValidPhoneNumber,
  type Country,
} from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useAmazonConnectAgent } from '@/hooks/useAmazonConnectAgent';
import { useAmazonConnectContacts } from '@/hooks/useAmazonConnectContacts';
import { connectAdapter } from '@/connect/adapter/streamsConnectAdapter';
import { hasActiveCallLeg } from '@/components/amazon-connect/contactUtils';
import { playDtmfTone } from '@/utils/dtmfTone';
import { Paper, Typography, Box, Button, Snackbar } from '@gapartners/ui';
import type { ConnectContact } from '@/types';

const KEYPAD_DIGITS = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '*',
  '0',
  '#',
];

const DEFAULT_COUNTRY: Country = 'US';

/**
 * Outbound dial panel: phone input (react-phone-number-input) + numeric keypad.
 * Shown only when the agent is not in an active call. Dial initiates agent.connect().
 */
export function OutboundDialPanel() {
  const { t } = useTranslation();
  const { agent } = useAmazonConnectAgent();
  const { activeContacts } = useAmazonConnectContacts();

  const activeContact = activeContacts.find(hasActiveCallLeg);
  const hasActiveCall = !!activeContact;

  const [value, setValue] = useState<string | undefined>(undefined);
  const [dialInProgress, setDialInProgress] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  // Always use US as default country for the phone input
  const defaultCountry: Country = DEFAULT_COUNTRY;

  const canOutbound = useMemo(() => {
    const permissions = agent?.getPermissions?.();
    return permissions?.includes('outboundCall') ?? false;
  }, [agent]);

  // Outbound dialpad contact that is still dialing (not yet connected).
  const dialingContact = useMemo(
    () =>
      activeContacts.find(contact => {
        try {
          return (
            connectAdapter.isDialContact(contact) && !hasActiveCallLeg(contact)
          );
        } catch {
          return false;
        }
      }) ?? null,
    [activeContacts]
  );

  const dialingContactRef = useRef<ConnectContact | null>(null);

  // When the dialing outbound contact disappears without connecting, tell the
  // agent why the "Connecting…" window closed (telecom disconnect, timeout,
  // wrong number, etc.). If it connected, hasActiveCall turns true and this
  // panel unmounts, so no toast is shown.
  useEffect(() => {
    if (dialingContact) {
      dialingContactRef.current = dialingContact;
      return;
    }
    const prev = dialingContactRef.current;
    if (!prev) return;
    dialingContactRef.current = null;

    let prevId: string | null = null;
    try {
      prevId = prev.getContactId();
    } catch {
      // contact already destroyed
    }
    const connected =
      prevId != null &&
      activeContacts.some(contact => {
        try {
          return contact.getContactId() === prevId && hasActiveCallLeg(contact);
        } catch {
          return false;
        }
      });
    if (!connected) {
      setToast({
        message: t('components.contactCenter.callEnded', 'Call ended'),
        type: 'error',
      });
    }
  }, [dialingContact, activeContacts, t]);

  const parsed = value ? parsePhoneNumber(value) : undefined;
  const isValid = value ? isValidPhoneNumber(value) : false;
  const canDial = isValid && !dialInProgress && canOutbound && !hasActiveCall;

  const handleKeypadPress = (digit: string) => {
    playDtmfTone(digit);
    if (!value) {
      // First digit: start with country code + digit
      const callingCode = getCountryCallingCode(defaultCountry);
      setValue('+' + callingCode + digit);
    } else {
      // Subsequent digits: append directly to avoid parsing issues with incomplete numbers
      setValue(value + digit);
    }
  };

  const handleDial = () => {
    if (!canDial || !parsed?.number) return;
    const e164 = parsed.number;
    setDialInProgress(true);
    connectAdapter.connectOutbound(e164, {
      success: () => {
        setDialInProgress(false);
        setValue(undefined);
      },
      failure: err => {
        setDialInProgress(false);
        setToast({
          message:
            err?.message ??
            t('components.contactCenter.callFailed', 'Call failed'),
          type: 'error',
        });
      },
    });
  };

  if (hasActiveCall) return null;
  if (!canOutbound) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Typography
        variant="h6"
        fontWeight={700}
        color="text.primary"
        sx={{ mb: 2, fontSize: '1.125rem' }}
      >
        {t('components.amazonConnect.outboundCall', 'Outbound Call')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
          {t('components.contactCenter.enterPhoneNumber', 'Enter phone number')}
        </Typography>
        <Box
          sx={{
            '& .PhoneInput': { display: 'flex', alignItems: 'center', gap: 1 },
            '& .PhoneInputInput': {
              flex: 1,
              minWidth: 0,
              padding: '10px 14px',
              fontSize: '1rem',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              '&:focus': { borderColor: 'primary.main', outline: 'none' },
            },
            '& .PhoneInputCountrySelect': {
              marginRight: 1,
              pointerEvents: 'none',
              cursor: 'default',
            },
            '& .PhoneInputCountrySelect button': {
              pointerEvents: 'none',
            },
          }}
        >
          <PhoneInput
            international
            countryCallingCodeEditable={false}
            defaultCountry={defaultCountry}
            placeholder={t(
              'components.contactCenter.enterPhoneNumber',
              'Enter phone number'
            )}
            value={value}
            onChange={(v: string | undefined) => setValue(v ?? undefined)}
            className="PhoneInput"
            inputClassName="PhoneInputInput"
          />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1,
            maxWidth: 240,
          }}
        >
          {KEYPAD_DIGITS.map(d => (
            <Button
              key={d}
              variant="outlined"
              onClick={() => handleKeypadPress(d)}
              disabled={dialInProgress}
              sx={{ minWidth: 56, fontSize: '1.25rem' }}
            >
              {d}
            </Button>
          ))}
        </Box>

        <Button
          variant="contained"
          color="primary"
          onClick={handleDial}
          disabled={!canDial}
        >
          {dialInProgress
            ? t('components.contactCenter.connecting', 'Connecting…')
            : t('components.contactCenter.dial', 'Dial')}
        </Button>
        {value && !isValid && (
          <Typography variant="caption" color="error.main">
            {t('components.contactCenter.invalidNumber', 'Invalid number')}
          </Typography>
        )}
      </Box>

      <Snackbar
        open={!!toast}
        onClose={() => setToast(null)}
        message={toast?.message}
        variant="toast"
        severity={toast?.type ?? 'info'}
        autoHideDuration={3000}
      />
    </Paper>
  );
}
